import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement} cartHeading - The cart heading text element.
 * @property {HTMLElement} moneyFormat - Template containing shop money format.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount', 'cartHeading', 'moneyFormat', 'currencySymbol'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();

    // Initialize heading based on current bubble state
    const initialCount = this.currentCartCount;
    this.updateHeading(initialCount);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
    this.updateHeading(itemCount);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  /**
   * Returns shop money format string from template ref.
   * @returns {string}
   */
  getMoneyFormat() {
    const tpl = this.#getRefText('moneyFormat');
    return tpl && tpl.length > 0 ? tpl : '{{amount}}';
  }

  /**
   * Returns currency symbol from template ref.
   * @returns {string}
   */
  getCurrencySymbol() {
    const tpl = this.#getRefText('moneyFormat');
    return tpl && tpl.length > 0 ? tpl : '';
  }

  /**
   * Formats a price (in cents) to a display string using Shopify formatter when available.
   * @param {number} cents
   * @returns {string}
   */
  formatTotal(cents) {
    // Format as requested: "TK <amount>" with two decimals, no thousands separator
    const amount = (Number(cents) / 100).toFixed(2);
    return `TK ${amount}`;
  }

  /**
   * Safely get trimmed textContent from a ref which may be an Element or an array of Elements.
   * @param {keyof Refs} name
   * @returns {string}
   */
  #getRefText(name) {
    const ref = this.refs[name];
    const el = Array.isArray(ref) ? ref[0] : ref;
    const text = el && /** @type {HTMLElement} */ (el).textContent;
    return (text ?? '').trim();
  }

  /**
   * Updates the heading text to show either "Cart" or the formatted total.
   * @param {number} itemCount
   */
  updateHeading = async (itemCount) => {
    const heading = this.refs.cartHeading;
    if (!(heading instanceof HTMLElement)) return;

    if (itemCount === 0) {
      heading.textContent = 'Cart';
      return;
    }

    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const formatted = this.formatTotal(cart.total_price);
      heading.textContent = formatted;
    } catch (_) {
      // If fetching fails, keep existing text
    }
  };

  /**
   * Local implementation of money formatting compatible with Shopify's placeholders.
   * @param {number} cents
   * @param {string} formatString
   * @returns {string}
   */
  formatMoneyLocal(cents, formatString) {
    /**
     * Format a cents value into a string with thousands and decimal separators.
     * @param {number} centsValue
     * @param {number} [precision=2]
     * @param {string} [thousands=',']
     * @param {string} [decimal='.']
     * @returns {string}
     */
    const formatWithDelimiters = (centsValue, precision = 2, thousands = ',', decimal = '.') => {
      const num = Number(centsValue);
      if (isNaN(num) || num == null) return '0';
      const amount = (Math.round(num) / 100).toFixed(precision);
      const [whole = '0', fraction = ''] = amount.split('.');
      const wholeWithSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? wholeWithSep + decimal + fraction : wholeWithSep;
    };

    const placeholderMatch = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch ? placeholderMatch[0] : '{{amount}}';
    const type = placeholderMatch ? placeholderMatch[1] : 'amount';

    let value = '';
    switch (type) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
    }

    return formatString.replace(placeholder, value);
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
